import React from 'react';
import { Box, Stepper, Step, StepLabel, Button, Container, Paper, Backdrop, CircularProgress, Skeleton } from '@mui/material';
import { Formik, Form } from 'formik';
import { FinancialYearStep } from '../steps/FinancialYearStep';
import { GSTDetailsStep } from '../steps/GSTDetailsStep';
import { VoucherNumberStep } from '../steps/VoucherNumberStep';
import { useCreateFinancialYear } from '../hooks/useCreateFinancialYear';
import { IntroPage } from './IntroPage';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import EnableEInvoiceModal from '../../../components/modals/EnableEInvoiceModal';

const labels = ['Financial Year', 'GST Registration Details', 'Voucher Number Formats'];

export const CreateFinancialYear: React.FC<{ onCancel: () => void }> = ({ onCancel }) => {
  const ctx = useCreateFinancialYear();

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 1, p1: 2, pr: 1 }}>
      {ctx.step > 0 && (
        <Paper elevation={2} sx={{ mb: 0.8, p: 2, borderRadius: 2 }}>
          <Stepper activeStep={ctx.step - 1} alternativeLabel>
            {labels.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      )}
      <Formik initialValues={{}} onSubmit={ctx.handleSubmit}>
        <Form>
          {ctx.step === 0 && <IntroPage next={ctx.next} cancel={()=>ctx.handleCancel({force:false})} />}

          {ctx.step > 0 && (
            <Paper elevation={1} sx={{ p: 1, borderRadius: 2 }}>
              {ctx.step === 1 && (
                <FinancialYearStep
                  state={ctx.state}
                  options={ctx.FINANCIAL_YEAR_OPTIONS}
                  selectedOption={ctx.selectedOption}
                  setSelectedOption={ctx.setSelectedOption}
                  setSelectedYear={ctx.setSelectedYear} 
                  error={ctx.error}
                  formErrors={ctx.formErrors}
                />
              )}
              {ctx.step === 2 && (
                <GSTDetailsStep
                  companyDetails={ctx.state.companyDetails!}
                  companyEntity={ctx.state.companyEntity!}
                  startYear={ctx.state.startYear}
                  endYear={ctx.state.endyear}
                  error={ctx.gstDetailsValidationErrors}
                  handleChange={ctx.handleChangeGSTDetails}
                  handleOnBlurGSTDetails={ctx.handleOnBlurGSTDetails}
                />
              )}
              {ctx.step === 3 && (
                <VoucherNumberStep
                  newFYData={{ financialyearid: ctx.state.financialYearToId, startyear: ctx.state.startYear, endyear: ctx.state.endyear }}
                />
              )}
              <Box mt={4} display="flex" justifyContent={{ xs: 'center', md: 'flex-end' }}>
                <Button variant="outlined" onClick={()=>ctx.handleCancel({force:false})} disabled={ctx.buttonLoading} sx={{ mr: 2, px: 6, py: 1 }}>
                  Cancel
                </Button>
                {ctx.step < 3 ? (
                  <Button disabled={ctx.fyAlreadyExists || ctx.buttonLoading} variant="contained" onClick={ctx.next} sx={{ px: 6, py: 1 }}>
                    {ctx.buttonLoading ? 'Processing...' : 'Next'}
                  </Button>
                ) : (
                  <Button variant="contained" type="submit" sx={{ px: 4, py: 1 }}>
                    Save
                  </Button>
                )}
              </Box>
            </Paper>
          )}
        </Form>
      </Formik>
      <ConfirmationModal
        open={ctx.confirmationModal}
        onClose={()=>ctx.handleConfirmOrCancel('cancel')}
        onConfirm={()=>ctx.handleConfirmOrCancel('confirm')}
        title={'Switch to New Financial Year'}
        message={"New Financial Year created. Switch to it now? Click 'Yes' to switch or 'No' to stay."}
      />
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={ctx.loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <EnableEInvoiceModal handleChangeGSTDetails={ctx.handleChangeGSTDetails} turnover={ctx.totalTurnOver} />
    </Container>
  );
};
